import { makeAutoObservable, runInAction } from 'mobx';

import type { CreatePay4MeCampaignUseCase } from '@/core/application/use-cases/pay4me/CreatePay4MeCampaignUseCase';
import type {
  Pay4MeCampaignDetailsResponse,
  Pay4MeCampaignResponse,
  Pay4MeContributionRequest,
  Pay4MeContributionResponse,
  Pay4MePSSImpactResponse,
  Pay4MeService,
} from '@/core/infrastructure/services/Pay4MeService';

export interface CampaignFormData {
  title: string;
  description: string;
  durationDays: number;
}

export class Pay4MeStore {
  campaigns: Pay4MeCampaignDetailsResponse[] = [];
  currentCampaign: Pay4MeCampaignDetailsResponse | null = null;
  campaignCreationResult: Pay4MeCampaignResponse | null = null;
  pssImpact: Pay4MePSSImpactResponse | null = null;
  contributionResult: Pay4MeContributionResponse | null = null;

  isLoading = false;
  isCreating = false;
  isContributing = false;
  error: string | null = null;

  constructor(
    private readonly createPay4MeCampaignUseCase: CreatePay4MeCampaignUseCase,
    private readonly pay4meService: Pay4MeService
  ) {
    makeAutoObservable(this);
  }

  get hasCampaign(): boolean {
    return this.currentCampaign !== null;
  }

  get campaignProgress(): number {
    return this.currentCampaign?.progress?.progressPercentage ?? 0;
  }

  get remainingAmount(): string {
    return this.currentCampaign?.progress?.remainingBalance ?? '0';
  }

  get canAcceptContributions(): boolean {
    if (!this.currentCampaign) return false;
    const status = (this.currentCampaign.status || '').toLowerCase();
    if (status !== 'active') return false;
    if (this.isExpired) return false;
    return this.campaignProgress < 100;
  }

  get isExpired(): boolean {
    const expiresAt = this.currentCampaign?.expiresAt;
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() < Date.now();
  }

  async createCampaign(orderId: string, formData: CampaignFormData, userId: string): Promise<void> {
    this.setCreating(true);
    this.setError(null);

    try {
      const result = await this.createPay4MeCampaignUseCase.execute({
        orderId,
        title: formData.title,
        description: formData.description,
        durationDays: formData.durationDays,
        userId,
      });

      runInAction(() => {
        this.campaignCreationResult = result;
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to create campaign');
      throw error;
    } finally {
      this.setCreating(false);
    }
  }

  async loadCampaignDetails(campaignId?: string, sharingToken?: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const campaign = await this.pay4meService.getCampaignDetails(campaignId, sharingToken);
      runInAction(() => {
        this.currentCampaign = campaign;
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to load campaign');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async contributeToCurrentCampaign(request: Pay4MeContributionRequest): Promise<void> {
    if (!this.currentCampaign) {
      throw new Error('No campaign loaded');
    }

    this.setContributing(true);
    this.setError(null);

    try {
      const result = await this.pay4meService.contributeToCampaign(this.currentCampaign.campaignId, request);
      runInAction(() => {
        this.contributionResult = result;
        if (this.currentCampaign && result.campaignProgress) {
          this.currentCampaign.progress = result.campaignProgress;
        }
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to process contribution');
      throw error;
    } finally {
      this.setContributing(false);
    }
  }

  async loadPSSImpact(campaignId: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const impact = await this.pay4meService.getPSSImpact(campaignId);
      runInAction(() => {
        this.pssImpact = impact;
      });
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Failed to load PSS impact');
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  clearCampaignCreationResult(): void {
    this.campaignCreationResult = null;
  }

  clearContributionResult(): void {
    this.contributionResult = null;
  }

  clearCurrentCampaign(): void {
    this.currentCampaign = null;
    this.pssImpact = null;
  }

  validateCampaignForm(formData: CampaignFormData): string[] {
    const errors: string[] = [];

    if (!formData.title || formData.title.trim().length < 5) {
      errors.push('Title must be at least 5 characters long');
    }
    if (formData.title.length > 100) {
      errors.push('Title cannot exceed 100 characters');
    }
    if (!formData.description || formData.description.trim().length < 10) {
      errors.push('Description must be at least 10 characters long');
    }
    if (formData.description.length > 500) {
      errors.push('Description cannot exceed 500 characters');
    }
    if (formData.durationDays < 1 || formData.durationDays > 90) {
      errors.push('Duration must be between 1 and 90 days');
    }

    return errors;
  }

  validateContribution(amount: number): string[] {
    const errors: string[] = [];
    if (!this.currentCampaign) {
      errors.push('No campaign selected');
      return errors;
    }

    const rules = this.currentCampaign.campaignRules;
    if (!rules) {
      errors.push('Campaign rules unavailable');
      return errors;
    }

    const minContribution = Number(rules.minContribution);
    const maxContribution = Number(rules.maxContribution);

    if (Number.isFinite(minContribution) && amount < minContribution) {
      errors.push(`Minimum contribution is GHS ${minContribution}`);
    }
    if (Number.isFinite(maxContribution) && amount > maxContribution) {
      errors.push(`Maximum contribution is GHS ${maxContribution}`);
    }
    if (!this.canAcceptContributions) {
      errors.push('Campaign is not accepting contributions');
    }

    return errors;
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private setCreating(creating: boolean): void {
    this.isCreating = creating;
  }

  private setContributing(contributing: boolean): void {
    this.isContributing = contributing;
  }

  private setError(message: string | null): void {
    this.error = message;
  }
}

